import { useEffect, useRef } from "react";
import api from "../../api/api";
import { practiceFeatures, practiceProfile } from "../../config/practice";

/*
 * Messaging as internal call notes (practiceFeatures().callNotes: 7825).
 *
 * OSA keeps its patient conversations as notes for the front desk: a "send"
 * writes chat_master + chat_details and nothing else. It never texts the
 * patient. care's own send (v1 schat, then patient/messaging/send-notification,
 * which SMSes a patient with no app) stays for 9928; for a call-notes practice
 * every send goes through postCallNote() instead -- v1
 * messaging/patientid/{pid}/send-message, the route OSA posts to, pinned to
 * v1 (`_gw`, see api/api.js) so a v2 routing rule never moves it -- and no
 * SMS route is ever called.
 *
 * The rest of this file is the OSA chat list (provider filter, tag chips,
 * tab rows, keep-selection, the 60-second list poll), for the two ChartList
 * copies (Communication > Messaging and the header chat).
 */

const DEFAULT_POLL_SECONDS = 60;

export function messagingProfile() {
  return practiceProfile().messaging || {};
}

// One call note for `patientid`: body `data` = JSON { source, message,
// loginuser } (send-message's form; it takes no attachment from us).
export function postCallNote(practiseid, patientid, message, loginuser) {
  const formData = new FormData();
  formData.append("data", JSON.stringify({ source: "practice", message, loginuser }));
  return api.post(`/v1/${practiseid}/messaging/patientid/${patientid}/send-message`, formData, {
    headers: { "Content-Type": "multipart/form-data" },
    _gw: "v1",
  });
}

// Quick-tag chips under the note box ("Spoke with patient", ...): profile data.
export function quickTags() {
  return messagingProfile().quickTags || [];
}

// A chip appends its text to what is typed ("<typed> - <tag>").
export function appendQuickTag(text, tag) {
  return text ? `${text} - ${tag}` : tag;
}

// Message tags that name a provider ("Dr Suresh"): the tag list's entries
// that start with profile.messaging.providerTagPrefix. Data-driven -- a new
// provider tag in message_tags shows up in the filter by itself.
export function isProviderTag(tag) {
  const prefix = messagingProfile().providerTagPrefix;
  return Boolean(prefix) && String(tag || "").trim().toLowerCase().startsWith(prefix.toLowerCase());
}

const tagsOf = (profile) =>
  typeof profile?.tags === "string"
    ? profile.tags.split(",").map((t) => t.trim()).filter(Boolean)
    : [];

const uniqueCI = (list) => {
  const seen = new Set();
  return list.filter((t) => {
    const k = t.toLowerCase();
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
};

// The provider filter's options: provider tags from the tag list and from the
// conversations themselves.
export function providerTagOptions(suggestions, profiles) {
  return uniqueCI([...(suggestions || []), ...(profiles || []).flatMap(tagsOf)].filter(isProviderTag));
}

// The tag chips: every other tag (OSA: the API's tags plus any tag found on
// a conversation), provider tags left to the provider filter.
export function chipTags(suggestions, profiles) {
  return uniqueCI([...(suggestions || []), ...(profiles || []).flatMap(tagsOf)].filter((t) => !isProviderTag(t)));
}

// Conversations matching the provider filter and any selected chip
// (case-insensitive substring of the comma-separated tags, as OSA).
export function filterChatProfiles(profiles, { tags = [], provider = "" } = {}) {
  let list = profiles || [];
  const has = (p, t) => typeof p.tags === "string" && p.tags.toLowerCase().includes(String(t).toLowerCase());
  if (provider) list = list.filter((p) => has(p, provider));
  if (tags.length) list = list.filter((p) => tags.some((t) => has(p, t)));
  return list;
}

// The Unread / All / Starred rows. care's rule: Unread = unread; Starred = read
// favourites; All = the rest. OSA's (osa): Starred = every favourite, and with
// a tag or provider filter on, All lists every match.
export function chatTabRows(rows, { osa = false, filtered = false } = {}) {
  const list = rows || [];
  return {
    unread: list.filter((x) => x.unread_v2 === 1),
    starred: list.filter((x) => x.is_favorite === 1 && (osa || x.unread_v2 === 0)),
    all:
      osa && filtered
        ? list
        : list.filter(
            (x) => !(x.unread_v2 === 0 && x.is_favorite === 1) && !(x.unread_v2 === 1 && x.is_reply === 0),
          ),
  };
}

// After a list refresh: the conversation on screen stays on screen (OSA: no
// jump to the first row). Its row is swapped for the fresh one only when that
// row changed, so the open thread reloads when something new arrived and not
// on every poll. Nothing selected stays nothing selected.
export function keepSelectedChat(patient, profiles) {
  if (!patient?.patientid) return patient;
  const fresh = (profiles || []).find((p) => String(p.patientid) === String(patient.patientid));
  if (!fresh) return patient;
  const changed = Object.keys(fresh).some((k) => fresh[k] !== patient[k]);
  return changed ? { ...fresh, flow: patient.flow ?? "" } : patient;
}

// Re-read the conversation list every profile.messaging.pollSeconds (60) while
// `enabled`. `tickRef.current` is read at every tick, so the tick always sees
// the list's current search text and date (OSA's own poll closed over the
// first render's and refreshed in the middle of a search). Ticks are skipped
// while the page is hidden; coming back to it refreshes at once.
export function useChatListPoll(enabled, tickRef) {
  const seconds = messagingProfile().pollSeconds || DEFAULT_POLL_SECONDS;
  const lastRef = useRef(0);
  useEffect(() => {
    if (!enabled) return undefined;
    const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";
    const tick = () => {
      if (hidden()) return;
      lastRef.current = Date.now();
      tickRef.current?.();
    };
    const id = setInterval(tick, seconds * 1000);
    const onVisible = () => {
      if (!hidden() && Date.now() - lastRef.current >= seconds * 1000) tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    lastRef.current = Date.now();
    return () => {
      clearInterval(id);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [enabled, seconds, tickRef]);
}

export const callNotesOn = () => Boolean(practiceFeatures().callNotes);
